import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrientationPref } from '../state/settings'
import { useEffectiveOrientation } from '../hooks/useViewportOrientation'

/** Time after the last preference change before the overlay fades to 0. */
const FADE_OUT_DELAY_MS = 1_200

/**
 * Phase 1.5.5: orientation preview overlay, mounted globally in App.tsx so
 * a Settings drawer change can show it regardless of the current route.
 *
 * W1 evolution from the original lobby-pinned faint-frame design:
 *  - position: fixed + z-index 40 so it covers underlying drawer scrim.
 *  - solid dim backdrop so the preview frame reads cleanly against any
 *    background.
 *  - **first-mount suppression**: the original useEffect on `[pref]` fired
 *    once at mount, so the overlay flashed on every app open. We now skip
 *    the very first effect run and only re-show on a real preference
 *    change.
 *  - pointer-events: none — never blocks underlying interaction.
 */
export default function OrientationPreview(): ReactElement | null {
  const { t } = useTranslation()
  const [pref] = useOrientationPref()
  const effective = useEffectiveOrientation()
  const [visible, setVisible] = useState(false)
  const firstRunRef = useRef(true)

  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false
      return
    }
    setVisible(true)
    const handle = window.setTimeout(() => setVisible(false), FADE_OUT_DELAY_MS)
    return () => {
      window.clearTimeout(handle)
    }
  }, [pref])

  const frame: 'landscape' | 'portrait' = pref === 'auto' ? effective : pref

  return (
    <div
      className={`orientation-preview${visible ? '' : ' orientation-preview--hidden'}`}
      aria-hidden="true"
      data-testid="orientation-preview"
    >
      <div className={`orientation-preview__frame orientation-preview__frame--${frame}`} />
      <p className="orientation-preview__hint">{t('settings.row.orientation')}</p>
    </div>
  )
}
